import { useEffect, useState } from "react";

import type { AppView } from "@/components/view-switch";

export type Section = "loop" | "supply";

export type Route = {
  readonly section: Section;
  /** Active sub-view within the Loop section. Ignored when section = supply. */
  readonly loopView: AppView;
};

const parse = (): Route => {
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  if (path === "/supply") return { section: "supply", loopView: "build" };
  if (path === "/positions") return { section: "loop", loopView: "positions" };
  return { section: "loop", loopView: "build" };
};

const toPath = (r: Route): string =>
  r.section === "supply"
    ? "/supply"
    : r.loopView === "positions"
      ? "/positions"
      : "/";

/**
 * Whole-app route mirrored into the URL path so links and refreshes are stable:
 *   / (default) · /positions · /supply
 * `go` accepts a partial route and folds it over the current one, pushing the
 * path via the History API; the `popstate` listener pulls it back into state,
 * keeping the two in sync on back/forward navigation. Deep links and refreshes
 * resolve because the host (Cloudflare assets / Vite dev) serves index.html as
 * the SPA fallback for unknown paths.
 */
export const usePathRoute = () => {
  const [route, setRoute] = useState<Route>(parse);

  useEffect(() => {
    const onPop = () => setRoute(parse());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const go = (next: Partial<Route>) => {
    const merged = { ...route, ...next };
    const path = toPath(merged);
    if (window.location.pathname !== path) {
      window.history.pushState(null, "", path);
    }
    setRoute(merged);
  };

  return { route, go } as const;
};
